import React from "react";
import { Box, CircularProgress, FormControl, MenuItem, Select, Typography } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";

const ACCENT = "#26890d";

// Dropdown that lets the user scope the whole dashboard down to a single cost
// center, or back out to the org-wide view via the "Organization" option
// (value ""). Purely presentational — the caller owns fetching the group list
// and resolving which group is currently selected.
const CostCenterSelector = ({ costCenterId, groups, loading, onChange }) => {
  const selectedGroup = groups.find((g) => g.cost_center_id === costCenterId) ?? null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 3,
        py: 2,
        mb: 3,
        backgroundColor: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: "12px",
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#555", whiteSpace: "nowrap" }}>
        Viewing data for
      </Typography>
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <Select
          value={costCenterId ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          displayEmpty
          renderValue={(value) => {
            if (!value) return "Organization (all cost centers)";
            const name = groups.find((g) => g.cost_center_id === value)?.cost_center_name;
            // The id→name mapping comes from the same groups list the dropdown
            // options are built from — until it's loaded (e.g. on first paint,
            // or when landing on a shared ?cost_center_id= link), show a
            // loading placeholder instead of the raw id string.
            return name ?? "Loading cost center…";
          }}
          sx={{ backgroundColor: "#f9f9f9", borderRadius: "6px", fontSize: "0.85rem" }}
        >
          <MenuItem value="" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            Organization (all cost centers)
          </MenuItem>
          {groups
            .filter((g) => g.cost_center_id)
            .map((g) => (
              <MenuItem key={g.cost_center_id} value={g.cost_center_id} sx={{ fontSize: "0.85rem" }}>
                {g.cost_center_name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress size={18} sx={{ color: ACCENT }} />}

      <Box
        sx={{
          ml: "auto",
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          px: 1.2,
          py: 0.35,
          backgroundColor: costCenterId ? "rgba(0,110,255,0.07)" : "rgba(38,137,13,0.07)",
          border: `1px solid ${costCenterId ? "rgba(0,110,255,0.25)" : "rgba(38,137,13,0.22)"}`,
          borderRadius: "20px",
        }}
      >
        <ApartmentIcon sx={{ fontSize: "13px", color: costCenterId ? "#006eff" : "#26890d" }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: costCenterId ? "#006eff" : "#26890d" }}>
          {costCenterId ? `Cost Center: ${selectedGroup?.cost_center_name ?? "Loading…"}` : "Organization-wide"}
        </Typography>
      </Box>
    </Box>
  );
};

export default CostCenterSelector;
